import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X, Paperclip, Download, ListChecks, FileText, Prohibit, Calendar,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Affiche le détail complet de la réponse DCE d'une entreprise :
 * lots chiffrés (PU + total par poste), commentaires, lots refusés,
 * pièces jointes, TVA par lot, totaux HT/TTC, date de soumission.
 *
 * Charge via GET /api/chantiers/{cid}/dce/{did}/reponses-detail/{ent_id}
 */
export default function EntrepriseReponseDetailModal({ chantierId, dceId, entrepriseId, entrepriseNom, invitationId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Sprint A — Préférer la route par invitation (gère le multi-invitations)
      const url = invitationId
        ? `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/reponses-detail-by-invitation/${invitationId}`
        : `${API_URL}/api/chantiers/${chantierId}/dce/${dceId}/reponses-detail/${entrepriseId}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [chantierId, dceId, entrepriseId, invitationId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="entreprise-reponse-detail-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            Détail de l'offre — {entrepriseNom}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="py-12 text-center text-sm text-[#9A9A9A]">Chargement…</div>}
        {err && <div className="py-8 text-center text-sm text-[#8B3A3A]">{err}</div>}

        {data && !loading && (
          <div className="space-y-5">
            {/* Bilan global */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border border-[#E0E0DB] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Total HT</p>
                <p className="text-lg font-bold text-[#1E3A5F]" data-testid="detail-total-ht">
                  {(data.montant_total_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
              <div className="border border-[#E0E0DB] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">TVA</p>
                <p className="text-lg font-bold text-[#6B6B6B]">
                  {(data.montant_total_tva || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
              <div className="border border-[#E0E0DB] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Total TTC</p>
                <p className="text-lg font-bold text-[#2D6A4F]" data-testid="detail-total-ttc">
                  {(data.montant_total_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
              <div className="border border-[#E0E0DB] p-3">
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Soumission</p>
                <p className="text-sm font-medium text-[#2C2C2C] flex items-center gap-1">
                  <Calendar size={12} />
                  {data.date_soumission ? new Date(data.date_soumission).toLocaleDateString('fr-FR') : '—'}
                </p>
                {data.delai_realisation_jours && (
                  <p className="text-[10px] text-[#6B6B6B] mt-1">Délai : {data.delai_realisation_jours} jours</p>
                )}
              </div>
            </div>

            {/* Lots refusés */}
            {data.lots_refuses?.length > 0 && (
              <div className="border-l-4 border-[#C4773B] bg-[#FFF3E0] p-3" data-testid="detail-lots-refuses">
                <p className="text-xs font-bold uppercase tracking-wider text-[#C4773B] mb-1 flex items-center gap-1">
                  <Prohibit size={12} weight="fill" /> {data.lots_refuses.length} lot(s) non soumis
                </p>
                <ul className="text-xs text-[#6B4A24] space-y-0.5">
                  {data.lots_refuses.map((l) => (
                    <li key={l.lot_id}>• {l.lot_nom}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Lots chiffrés */}
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#1E3A5F] mb-3 flex items-center gap-2">
                <ListChecks size={14} /> Lots chiffrés ({data.lots_chiffres?.length || 0})
              </h3>
              <div className="space-y-3">
                {(data.lots_chiffres || []).map((lot) => (
                  <div key={lot.lot_id} className="border border-[#E0E0DB]" data-testid={`detail-lot-${lot.lot_id}`}>
                    <div className="bg-[#F5F5F3] px-3 py-2 flex items-center justify-between">
                      <span className="font-semibold text-[#2C2C2C]">{lot.lot_nom}</span>
                      <div className="flex items-center gap-3 text-xs">
                        {lot.tva_pct !== null && lot.tva_pct !== undefined && (
                          <span className="text-[#6B6B6B]">TVA {lot.tva_pct}%</span>
                        )}
                        <span className="font-bold text-[#1E3A5F]">
                          {(lot.total_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT
                        </span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-[#FAFAF9] text-[#6B6B6B]">
                          <tr>
                            <th className="text-left px-3 py-2">Poste</th>
                            <th className="text-center px-3 py-2 w-16">Qté</th>
                            <th className="text-center px-3 py-2 w-12">Unité</th>
                            <th className="text-right px-3 py-2 w-24">PU HT</th>
                            <th className="text-right px-3 py-2 w-28">Total HT</th>
                            <th className="text-left px-3 py-2 w-48">Commentaire</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(lot.postes || []).map((p) => (
                            <tr key={p.poste_id} className="border-t border-[#E0E0DB]" data-testid={`detail-poste-${p.poste_id}`}>
                              <td className="px-3 py-2">{p.designation}</td>
                              <td className="px-3 py-2 text-center text-[#6B6B6B]">{p.quantite}</td>
                              <td className="px-3 py-2 text-center text-[#6B6B6B]">{p.unite}</td>
                              <td className="px-3 py-2 text-right">
                                {(p.prix_unitaire_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                              </td>
                              <td className="px-3 py-2 text-right font-medium">
                                {(p.total_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                              </td>
                              <td className="px-3 py-2 text-[#6B6B6B] italic">{p.commentaire || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Variantes */}
            {data.variantes?.length > 0 && (
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#D97706] mb-2">
                  Variantes proposées ({data.variantes.length})
                </h3>
                <div className="space-y-2">
                  {data.variantes.map((v, idx) => (
                    <div key={idx} className="border border-[#F5D5B3] bg-[#FFF8E1] p-3" data-testid={`detail-variante-${idx}`}>
                      <div className="flex justify-between text-xs">
                        <span className="font-medium text-[#6B4A24]">Lot : {v.lot_nom || v.lot_id}</span>
                        <span className="font-bold text-[#D97706]">
                          {(v.montant_ht_total || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT
                        </span>
                      </div>
                      <p className="text-xs text-[#6B4A24] mt-1 whitespace-pre-wrap">{v.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mémoire technique */}
            {data.memoire_technique_libre && (
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#1E3A5F] mb-2 flex items-center gap-2">
                  <FileText size={14} /> Mémoire technique
                </h3>
                <div className="border border-[#E0E0DB] p-3 bg-[#FAFAF9] text-xs leading-relaxed whitespace-pre-wrap" data-testid="detail-memoire-tech">
                  {data.memoire_technique_libre}
                </div>
              </div>
            )}

            {/* Pièces jointes */}
            {data.pieces_jointes?.length > 0 && (
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#1E3A5F] mb-2 flex items-center gap-2">
                  <Paperclip size={14} /> Pièces jointes ({data.pieces_jointes.length})
                </h3>
                <ul className="space-y-1">
                  {data.pieces_jointes.map((pj) => (
                    <li key={pj.id} className="flex items-center justify-between border border-[#E0E0DB] px-3 py-2 text-xs hover:bg-[#FAFAF9]" data-testid={`detail-pj-${pj.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip size={12} className="text-[#6B6B6B] flex-shrink-0" />
                        <span className="text-[10px] uppercase font-bold text-[#1E3A5F] tracking-wider flex-shrink-0">
                          {pj.type?.replace('_', ' ') || 'autre'}
                        </span>
                        <span className="truncate">{pj.filename}</span>
                        <span className="text-[#9A9A9A] flex-shrink-0">
                          ({pj.size ? `${(pj.size / 1024).toFixed(0)} ko` : ''})
                        </span>
                      </div>
                      <a
                        href={pj.url ? (pj.url.startsWith('http') ? pj.url : `${API_URL}${pj.url}`) : '#'}
                        target="_blank" rel="noreferrer"
                        className="text-[#1E3A5F] hover:text-[#C75B39] flex items-center gap-1"
                        data-testid={`detail-pj-download-${pj.id}`}
                      >
                        <Download size={12} /> Télécharger
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
